import React from 'react';
import BrandedAlertModal from '../BrandedAlertModal/BrandedAlertModal';
import type { BrandedAction } from '../BrandedAlertModal/BrandedAlertModal';

export type PaymentMethodSuccessVariant = 'saved' | 'revalidated';

export interface PaymentMethodSuccessModalProps {
  isOpen: boolean;
  variant?: PaymentMethodSuccessVariant;
  /** Shown when variant is `revalidated` (e.g. "Active", "Declined"). */
  cardStatusLabel?: string;
  onConfirm: () => void;
  onClose?: () => void;
}

const CardHeaderIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <rect x="3" y="6" width="18" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3 10h18" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const CardSuccessIllustration: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <rect x="2.5" y="6" width="19" height="13" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M2.5 10h19" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M9 18.5l2 2 4-4"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PaymentMethodSuccessModal: React.FC<PaymentMethodSuccessModalProps> = ({
  isOpen,
  variant = 'saved',
  cardStatusLabel,
  onConfirm,
  onClose,
}) => {
  const handleClose = onClose ?? onConfirm;
  const isRevalidated = variant === 'revalidated';

  const title = isRevalidated ? 'Card re-validated' : 'Payment method saved';
  const ariaLabel = isRevalidated ? 'Card re-validated' : 'Payment method saved';

  const actions: BrandedAction[] = [
    { label: 'OK', variant: 'primary', onClick: onConfirm },
  ];

  return (
    <BrandedAlertModal
      isOpen={isOpen}
      onClose={handleClose}
      headerLabel="Payment Method"
      headerIcon={<CardHeaderIcon />}
      illustration={<CardSuccessIllustration />}
      illustrationVariant="success"
      title={title}
      ariaLabel={ariaLabel}
      actions={actions}
    >
      {isRevalidated ? (
        <>
          Your card has been re-validated successfully.
          {cardStatusLabel ? (
            <>
              {' '}
              Current status: <strong>{cardStatusLabel}</strong>.
            </>
          ) : null}
          <br />
          <br />
          You can now post shifts and accept offers for this location.
        </>
      ) : (
        <>
          Your payment method was successfully updated and is on file for this location.
          <br />
          <br />
          TempStars only charges a flat placement fee after a temping shift is completed — no
          hourly mark-ups and no hiring fees. You&apos;re all set to post shifts and book talent.
        </>
      )}
    </BrandedAlertModal>
  );
};

export default PaymentMethodSuccessModal;
